import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError, formatBytes } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Smartphone, Monitor, Square as SquareIcon, Image as ImageIcon, Video, Music, UploadCloud, Loader2, Sparkles, X, RotateCw, ArrowRight, Trash2, Play, Pause, Check } from "lucide-react";
import { toast } from "sonner";
import RenderDiagnostics from "./RenderDiagnostics";

const TYPES = [
  { v: "vertical", label: "Vertical Reel", sub: "1080×1920 · 9:16", icon: Smartphone, durations: [15, 30, 45, 60] },
  { v: "landscape", label: "Landscape Reel", sub: "1920×1080 · 16:9", icon: Monitor, durations: [30, 60, 90, 120] },
  { v: "square", label: "Square Social", sub: "1080×1080 · 1:1", icon: SquareIcon, durations: [15, 30, 60] },
  { v: "image_gif", label: "Photo GIF", sub: "Animated from photos", icon: ImageIcon },
  { v: "video_gif", label: "Video GIF", sub: "From a video section", icon: Video },
];

const ACTIVE = ["queued", "preparing", "rendering", "optimising", "uploading"];

export default function ReelBuilderPanel({ event, reload, goNext }) {
  const [type, setType] = useState("vertical");
  const [platform, setPlatform] = useState("");
  const [duration, setDuration] = useState(30);
  const [mode, setMode] = useState("automatic");
  const [fit, setFit] = useState(event.design?.fit_mode || "crop");
  const [aspect, setAspect] = useState("1:1");
  const [imageDuration, setImageDuration] = useState(0.8);
  const [loopCount, setLoopCount] = useState(0);
  const [gifWidth, setGifWidth] = useState(480);
  const [optimise, setOptimise] = useState(true);
  const [videoAsset, setVideoAsset] = useState("");
  const [vStart, setVStart] = useState(0);
  const [vEnd, setVEnd] = useState(5);
  const [fps, setFps] = useState(15);
  const [videos, setVideos] = useState([]);
  const [music, setMusic] = useState([]);
  const [library, setLibrary] = useState([]);
  const [musicId, setMusicId] = useState("none");
  const [volume, setVolume] = useState(0.85);
  const [fadeIn, setFadeIn] = useState(1);
  const [fadeOut, setFadeOut] = useState(2);
  const [keepAudio, setKeepAudio] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const musicRef = useRef();
  const [musicCat, setMusicCat] = useState("All");
  const [playingId, setPlayingId] = useState(null);
  const previewRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [m, mu, r, lib] = await Promise.all([
        api.get(`/events/${event.id}/media`),
        api.get(`/events/${event.id}/music`),
        api.get(`/events/${event.id}/renders`),
        api.get(`/music/library`),
      ]);
      setVideos(m.data.filter((a) => a.kind === "video"));
      setMusic(mu.data);
      setLibrary(lib.data || []);
      setJobs(r.data.jobs);
    } catch (e) { toast.error(apiError(e, "Could not load reel builder data")); }
  }, [event.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // poll while active
  useEffect(() => {
    const hasActive = jobs.some((j) => ACTIVE.includes(j.status));
    if (!hasActive) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/events/${event.id}/renders`);
        setJobs(data.jobs);
        if (!data.jobs.some((j) => ACTIVE.includes(j.status))) reload();
      } catch (e) {}
    }, 2500);
    return () => clearInterval(t);
  }, [jobs, event.id, reload]);

  const uploadMusic = async (file) => {
    setUploadingMusic(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/events/${event.id}/music`, fd); setMusicId(data.id); loadAll(); toast.success("Music uploaded"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setUploadingMusic(false); }
  };

  const buildSettings = () => {
    const base = { template: event.design?.template || "custom", name: platform ? `${platform}` : undefined };
    if (type === "image_gif") return { ...base, aspect, image_duration: imageDuration, loop_count: loopCount, width: gifWidth, optimise, fit_mode: fit };
    if (type === "video_gif") return { ...base, asset_id: videoAsset, start: vStart, end: vEnd, fps, width: gifWidth, optimise };
    const s = { ...base, duration, mode, fit_mode: fit };
    if (musicId !== "none") { s.music_id = musicId; s.music_volume = volume; s.music_fade_in = fadeIn; s.music_fade_out = fadeOut; s.music_loop = true; s.keep_original_audio = keepAudio; }
    return s;
  };

  const create = async () => {
    if (type === "video_gif" && !videoAsset) return toast.error("Select a video clip for the video GIF");
    setSubmitting(true);
    try {
      await api.post(`/events/${event.id}/render`, { output_type: type, settings: buildSettings() });
      toast.success("Render started — you can leave this page and come back.");
      loadAll(); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  const cancel = async (id) => { try { await api.post(`/render/jobs/${id}/cancel`); loadAll(); } catch (e) {} };
  const retry = async (id) => { try { await api.post(`/render/jobs/${id}/retry`); loadAll(); } catch (e) {} };

  const trackUrl = (id) => `${api.defaults.baseURL}/music/${id}/file?token=${localStorage.getItem("csrm_token")}`;
  const togglePlay = (id) => {
    let a = previewRef.current;
    if (!a) { a = new Audio(); previewRef.current = a; a.onended = () => setPlayingId(null); }
    if (playingId === id) { a.pause(); setPlayingId(null); return; }
    a.src = trackUrl(id);
    a.volume = volume;
    a.play().then(() => setPlayingId(id)).catch(() => { setPlayingId(null); toast.error("Could not play preview"); });
  };
  useEffect(() => { if (previewRef.current) previewRef.current.volume = volume; }, [volume]);
  useEffect(() => () => { if (previewRef.current) previewRef.current.pause(); }, []);
  const categories = ["All", ...Array.from(new Set(library.map((m) => m.category).filter(Boolean)))];
  const filteredLibrary = musicCat === "All" ? library : library.filter((m) => m.category === musicCat);

  const [packBusy, setPackBusy] = useState(false);
  const [includeMontage, setIncludeMontage] = useState(true);
  const [montageLayout, setMontageLayout] = useState("grid_4");
  const createPack = async () => {
    setPackBusy(true);
    try {
      const body = { include_montage: includeMontage, montage_layout: montageLayout };
      if (musicId !== "none") body.music_id = musicId;
      const { data } = await api.post(`/events/${event.id}/highlights-pack`, body);
      const extra = data.montage ? " A best-of montage scene was added too." : "";
      toast.success(`Highlights Pack started — ${data.jobs.length} formats rendering.${extra}`);
      loadAll(); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setPackBusy(false); }
  };

  const current = TYPES.find((t) => t.v === type);
  const isReel = ["vertical", "landscape", "square"].includes(type);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-ivory">Create Reel</h2>
          <p className="text-muted-foreground text-sm mt-1">Pick a format and start a real background render. Rendering continues even if you leave.</p>
        </div>
        <Button data-testid="build-next-button" onClick={goNext} variant="outline" className="rounded-full border-gold text-gold hover:bg-gold/10">
          Review & Share <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>

      {/* One-click Highlights Pack */}
      <div className="rounded-2xl border border-gold/40 bg-gradient-to-br from-gold/10 to-transparent p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-xl text-ivory flex items-center gap-2"><Sparkles className="w-5 h-5 text-gold" /> Event Highlights Pack</h3>
            <p className="text-sm text-muted-foreground mt-1">Generate everything in one go — a 9:16 Reel, a 15s Story, a Square post, a Landscape reel and a Photo GIF. Uses your selected photos{musicId !== "none" ? " and the chosen music" : ""}.</p>
          </div>
          <Button data-testid="highlights-pack-button" onClick={createPack} disabled={packBusy}
            className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold h-12 px-6 shrink-0 gold-glow">
            {packBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Sparkles className="w-5 h-5 mr-2" /> Create Highlights Pack</>}
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gold/20 pt-3">
          <div className="flex items-center gap-2">
            <Switch id="include-montage" data-testid="include-montage-toggle" checked={includeMontage} onCheckedChange={setIncludeMontage} />
            <Label htmlFor="include-montage" className="text-sm text-champagne cursor-pointer">Add a best-of montage scene</Label>
          </div>
          {includeMontage && (
            <Select value={montageLayout} onValueChange={setMontageLayout}>
              <SelectTrigger data-testid="montage-layout-select" className="w-44 h-9 bg-navy-deep border-navy-border"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="grid_4" data-testid="montage-grid_4">Grid montage</SelectItem>
                <SelectItem value="polaroid_3" data-testid="montage-polaroid_3">Polaroid montage</SelectItem>
                <SelectItem value="split_2" data-testid="montage-split_2">Split-screen</SelectItem>
                <SelectItem value="strip_4" data-testid="montage-strip_4">Photo strip</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Platform quick presets */}
      <div>
        <div className="text-champagne text-xs uppercase tracking-wider mb-2">Post it to…</div>
        <div className="flex flex-wrap gap-2">
          {[
            { k: "Instagram Reel", type: "vertical", dur: 30 },
            { k: "Instagram Story", type: "vertical", dur: 15 },
            { k: "Instagram Post", type: "square", dur: 30 },
            { k: "TikTok", type: "vertical", dur: 30 },
          ].map((p) => (
            <button key={p.k} data-testid={`preset-${p.k.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => { setType(p.type); setDuration(p.dur); setPlatform(p.k); }}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${platform === p.k ? "bg-gold text-navy border-gold" : "border-navy-border text-champagne hover:border-gold"}`}>
              {p.k}
            </button>
          ))}
        </div>
      </div>

      {/* Type selector */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {TYPES.map((t) => (
          <button key={t.v} data-testid={`output-type-${t.v}`} onClick={() => { setType(t.v); setPlatform(""); if (t.durations) setDuration(t.durations[1]); }}
            className={`rounded-xl border p-4 text-left transition-all hover:-translate-y-0.5 ${type === t.v ? "border-gold bg-gold/10" : "border-navy-border bg-navy-card"}`}>
            <t.icon className={`w-6 h-6 mb-2 ${type === t.v ? "text-gold" : "text-champagne"}`} />
            <div className="text-ivory text-sm font-medium">{t.label}</div>
            <div className="text-[11px] text-muted-foreground">{t.sub}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Settings */}
        <div className="bg-navy-card border border-navy-border rounded-2xl p-5 space-y-4">
          <h3 className="text-champagne text-sm uppercase tracking-wider">Settings</h3>

          {isReel && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs text-champagne">Duration</Label>
                <div className="flex flex-wrap gap-2">
                  {current.durations.map((d) => (
                    <button key={d} data-testid={`duration-${d}`} onClick={() => setDuration(d)}
                      className={`px-4 py-2 rounded-full text-sm border transition-colors ${duration === d ? "bg-gold text-navy border-gold" : "border-navy-border text-champagne hover:border-gold"}`}>
                      {d >= 60 ? `${d / 60}m` : `${d}s`}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-champagne">Creation mode</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger data-testid="mode-select" className="bg-navy-deep border-navy-border h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual — my timeline order</SelectItem>
                    <SelectItem value="automatic">Automatic — fit to duration</SelectItem>
                    <SelectItem value="smart_shuffle">Smart Shuffle — favourites first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-champagne">Media fit</Label>
                <Select value={fit} onValueChange={setFit}>
                  <SelectTrigger data-testid="fit-mode-select" className="bg-navy-deep border-navy-border h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="crop">Smart centre crop</SelectItem>
                    <SelectItem value="blur">Fit with blurred background</SelectItem>
                    <SelectItem value="pad">Fit with solid background</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {type === "image_gif" && (
            <>
              <div className="space-y-1.5"><Label className="text-xs text-champagne">Aspect ratio</Label>
                <Select value={aspect} onValueChange={setAspect}>
                  <SelectTrigger data-testid="gif-aspect-select" className="bg-navy-deep border-navy-border h-10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="1:1">1:1 Square</SelectItem><SelectItem value="4:5">4:5 Portrait</SelectItem><SelectItem value="9:16">9:16 Vertical</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="text-xs text-champagne">Seconds per photo: {imageDuration}s</Label>
                <Slider data-testid="gif-image-duration" value={[imageDuration]} min={0.3} max={2} step={0.1} onValueChange={([v]) => setImageDuration(v)} className="pt-3" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-champagne">Loop count (0 = infinite)</Label>
                <Input data-testid="gif-loop-count" type="number" min="0" value={loopCount} onChange={(e) => setLoopCount(parseInt(e.target.value) || 0)} className="bg-navy-deep border-navy-border h-10" /></div>
            </>
          )}

          {type === "video_gif" && (
            <>
              <div className="space-y-1.5"><Label className="text-xs text-champagne">Video clip</Label>
                <Select value={videoAsset} onValueChange={(v) => { setVideoAsset(v); const a = videos.find((x) => x.id === v); if (a) setVEnd(Math.min(10, a.duration || 5)); }}>
                  <SelectTrigger data-testid="video-gif-asset-select" className="bg-navy-deep border-navy-border h-10"><SelectValue placeholder="Select a clip" /></SelectTrigger>
                  <SelectContent>{videos.map((v) => <SelectItem key={v.id} value={v.id}>{v.original_filename}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs text-champagne">Start (s)</Label><Input data-testid="vgif-start" type="number" min="0" step="0.5" value={vStart} onChange={(e) => setVStart(parseFloat(e.target.value) || 0)} className="bg-navy-deep border-navy-border h-10" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-champagne">End (s, max +10)</Label><Input data-testid="vgif-end" type="number" min="0" step="0.5" value={vEnd} onChange={(e) => setVEnd(parseFloat(e.target.value) || 0)} className="bg-navy-deep border-navy-border h-10" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs text-champagne">Frame rate: {fps} fps</Label>
                <Slider data-testid="vgif-fps" value={[fps]} min={8} max={24} step={1} onValueChange={([v]) => setFps(v)} className="pt-3" /></div>
            </>
          )}

          {(type === "image_gif" || type === "video_gif") && (
            <>
              <div className="space-y-1.5"><Label className="text-xs text-champagne">Width: {gifWidth}px</Label>
                <Slider data-testid="gif-width" value={[gifWidth]} min={240} max={720} step={20} onValueChange={([v]) => setGifWidth(v)} className="pt-3" /></div>
              <div className="flex items-center justify-between"><Label className="text-sm text-champagne">Optimise file size</Label>
                <Switch data-testid="gif-optimise" checked={optimise} onCheckedChange={setOptimise} /></div>
            </>
          )}
        </div>

        {/* Music (reels only) */}
        <div className="bg-navy-card border border-navy-border rounded-2xl p-5 space-y-4">
          <h3 className="text-champagne text-sm uppercase tracking-wider flex items-center gap-2"><Music className="w-4 h-4" /> Music</h3>
          {isReel ? (
            <>
              <div className="rounded-lg bg-amber-950/30 border border-amber-900/40 p-3 text-xs text-amber-200">
                Only upload music you have permission to use. Do not upload copyrighted commercial tracks.
              </div>
              <input ref={musicRef} data-testid="music-file-input" type="file" accept=".mp3,.wav,.m4a" className="hidden" onChange={(e) => e.target.files?.[0] && uploadMusic(e.target.files[0])} />
              <Button data-testid="upload-music-button" variant="outline" onClick={() => musicRef.current?.click()} disabled={uploadingMusic} className="border-navy-border w-full">
                {uploadingMusic ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UploadCloud className="w-4 h-4 mr-1.5" /> Upload MP3 / WAV / M4A</>}
              </Button>

              {/* Library browser with mood categories + play/pause previews */}
              {library.length > 0 && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5" data-testid="music-category-chips">
                    {categories.map((c) => (
                      <button key={c} type="button" data-testid={`music-cat-${c.toLowerCase()}`} onClick={() => setMusicCat(c)}
                        className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${musicCat === c ? "bg-gold text-navy border-gold" : "border-navy-border text-champagne hover:border-gold"}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                  <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1" data-testid="music-library-list">
                    {filteredLibrary.map((m) => (
                      <div key={m.id} data-testid={`music-track-${m.id}`}
                        className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${musicId === m.id ? "border-gold bg-gold/10" : "border-navy-border hover:border-gold/50"}`}
                        onClick={() => setMusicId(m.id)}>
                        <button type="button" data-testid={`play-track-${m.id}`} onClick={(e) => { e.stopPropagation(); togglePlay(m.id); }}
                          className="w-8 h-8 rounded-full bg-navy-deep border border-navy-border flex items-center justify-center text-gold shrink-0 hover:bg-gold hover:text-navy transition-colors">
                          {playingId === m.id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <p className="text-ivory text-xs truncate">{m.name}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{m.category ? m.category : ""}{m.mood ? ` · ${m.mood}` : ""}</p>
                        </div>
                        {musicId === m.id && <Check className="w-4 h-4 text-gold shrink-0" />}
                      </div>
                    ))}
                    {filteredLibrary.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">No tracks in this mood.</p>}
                  </div>
                </div>
              )}

              <Select value={musicId} onValueChange={setMusicId}>
                <SelectTrigger data-testid="music-select" className="bg-navy-deep border-navy-border h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No music</SelectItem>
                  {library.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="text-gold">Clyde Side Library (royalty-free)</SelectLabel>
                      {library.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`library-track-${m.id}`}>{m.name}{m.mood ? ` — ${m.mood}` : ""}</SelectItem>)}
                    </SelectGroup>
                  )}
                  {music.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Uploaded for this event</SelectLabel>
                      {music.map((m) => <SelectItem key={m.id} value={m.id}>{m.original_filename}</SelectItem>)}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              {musicId !== "none" && (
                <>
                  <audio data-testid="music-preview" controls src={`${api.defaults.baseURL}/music/${musicId}/file?token=${localStorage.getItem("csrm_token")}`} className="w-full h-9" />
                  <div className="space-y-1.5"><Label className="text-xs text-champagne">Volume: {Math.round(volume * 100)}%</Label>
                    <Slider data-testid="music-volume" value={[volume * 100]} min={0} max={100} step={5} onValueChange={([v]) => setVolume(v / 100)} className="pt-3" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs text-champagne">Fade in: {fadeIn}s</Label>
                      <Slider data-testid="music-fadein" value={[fadeIn]} min={0} max={5} step={0.5} onValueChange={([v]) => setFadeIn(v)} className="pt-3" /></div>
                    <div className="space-y-1.5"><Label className="text-xs text-champagne">Fade out: {fadeOut}s</Label>
                      <Slider data-testid="music-fadeout" value={[fadeOut]} min={0} max={5} step={0.5} onValueChange={([v]) => setFadeOut(v)} className="pt-3" /></div>
                  </div>
                  <div className="flex items-center justify-between"><Label className="text-sm text-champagne">Mix with original clip audio</Label>
                    <Switch data-testid="keep-audio-switch" checked={keepAudio} onCheckedChange={setKeepAudio} /></div>
                </>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Music applies to video reels. GIFs have no audio.</p>
          )}
        </div>
      </div>

      <Button data-testid="create-reel-button" onClick={create} disabled={submitting}
        className="w-full h-14 rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold text-lg gold-glow">
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Sparkles className="w-5 h-5 mr-2" /> Create {current.label}</>}
      </Button>

      {/* Jobs */}
      {jobs.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-champagne text-sm uppercase tracking-wider">Render queue</h3>
          {jobs.slice(0, 8).map((j) => (
            <div key={j.id} data-testid={`job-${j.id}`} className="bg-navy-card border border-navy-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-ivory text-sm capitalize">{j.output_type.replace("_", " ")}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${j.status === "complete" ? "bg-emerald-500/20 text-emerald-300" : j.status === "failed" ? "bg-red-500/20 text-red-300" : j.status === "cancelled" ? "bg-zinc-500/20 text-zinc-300" : "bg-amber-500/20 text-amber-300"}`}>
                    {j.status}
                  </span>
                </div>
                <div className="flex gap-2">
                  {ACTIVE.includes(j.status) && <button data-testid={`cancel-job-${j.id}`} onClick={() => cancel(j.id)} className="text-muted-foreground hover:text-red-400"><X className="w-4 h-4" /></button>}
                  {j.status === "failed" && <button data-testid={`retry-job-${j.id}`} onClick={() => retry(j.id)} className="text-gold flex items-center text-xs"><RotateCw className="w-4 h-4 mr-1" /> Retry</button>}
                </div>
              </div>
              <Progress value={j.progress} className="h-1.5" />
              <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
                <span>{j.message}</span><span>{j.progress}%</span>
              </div>
              {j.error && <p className="text-xs text-red-400 mt-1">{j.error}</p>}
            </div>
          ))}
        </div>
      )}

      <RenderDiagnostics />
    </div>
  );
}
